/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { usePhotoUriForLayoutSize } from '@kaiizen/kai-pro-api-react-state';
import type { ScanPhoto } from '@kaiizen/kai-scan-template-system';

import { formatDate } from '@/Client/Presenters';
import { styleguideColors as COLORS } from '@/Client/Styles/kaiShikiTokens';
import { styleguideFontFamily as FONT, withOpacity } from '@kaiizen/kai-shiki/core';
import { GalleryMediaTile } from '@kaiizen/kai-shiki/react';

import { pxToRem } from '@/Client/Styles/pxToRem';
import { type GalleryPhotoOccurrence } from '../galleryPhotoOperations';
import type { PhotoManagerSection } from '../photoManagerModel';
import { buildOccurrence } from './photoManagerLib';

const PHOTO_FIGURE_STYLE = css`
  label: PHOTO_MANAGER_TILE_FIGURE;
  display: flex;
  flex-direction: column;
  gap: 5px;
  margin: 0;
  min-width: 0;
`;

const PHOTO_TILE_WRAP_STYLE = css`
  position: relative;
  min-width: 0;
`;

/**
 * Mockup `.pm-check`: trailing top corner, white until selected, accent behind the tick once
 * it is. Drawn here rather than via GalleryMediaTile's built-in control, which seats itself
 * at the leading edge — the mockup's geometry wins in this surface.
 */

const TILE_CHECK_STYLE = css`
  label: PHOTO_MANAGER_TILE_CHECK;
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 5;
  width: 22px;
  height: 22px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  background: ${COLORS.white};
  border: 1.5px solid ${COLORS.ink};
  color: transparent;
  font-size: ${pxToRem(12)};
  line-height: 1;
  cursor: pointer;

  &[aria-pressed='true'] {
    background: ${COLORS.accent};
    color: ${COLORS.ink};
  }
`;

/** The date rides under the tile, matching the mockup's `.pm-tile .pgd-photo-date`. */

const TILE_DATE_STYLE = css`
  label: PHOTO_MANAGER_TILE_DATE;
  font-family: ${FONT.body};
  font-size: ${pxToRem(12)};
  color: ${withOpacity(COLORS.white, 0.62)};
  white-space: nowrap;
`;

export const PhotoTile = ({
  photo,
  section,
  index,
  scanIsEditable,
  selected,
  onToggleSelection,
  onOpenPhoto,
}: {
  photo: ScanPhoto;
  section: PhotoManagerSection;
  index: number;
  scanIsEditable: boolean;
  selected: boolean;
  onToggleSelection: (occurrence: GalleryPhotoOccurrence) => void;
  onOpenPhoto: (occurrence: GalleryPhotoOccurrence) => void;
}) => {
  // An auto-fill grid of 4:3 tiles at least 170px wide: about 190 to 220px on a desktop takeover.
  const imageUrl = usePhotoUriForLayoutSize(photo.photoId, 220);
  const label = `${section.title} photo ${index + 1}`;
  const occurrence = buildOccurrence(photo, section);
  const dateLabel = photo.addedAt ? formatDate(photo.addedAt) : undefined;
  return (
    <figure css={PHOTO_FIGURE_STYLE}>
      <span css={PHOTO_TILE_WRAP_STYLE}>
        <GalleryMediaTile
          model={{
            id: photo.photoId,
            label,
            imageUrl,
            selected,
          }}
          onOpen={() => onOpenPhoto(occurrence)}
          showSelectionControl={false}
        />
        {scanIsEditable && (
          <button
            aria-label={`${selected ? 'Deselect' : 'Select'} ${label}`}
            aria-pressed={selected}
            css={TILE_CHECK_STYLE}
            onClick={() => onToggleSelection(occurrence)}
            type='button'
          >
            ✓
          </button>
        )}
      </span>
      {dateLabel && <span css={TILE_DATE_STYLE}>{dateLabel}</span>}
    </figure>
  );
};
